import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const ZONE_OPTIONS = [
  {
    code: "CBD",
    label: "Harare CBD",
    hint: "Gulf Complex, Ximex, Joina City, First Street",
  },
  {
    code: "AVONDALE",
    label: "Avondale / Sam Levy's",
    hint: "Sam Levy's Village, Avondale Shopping Centre",
  },
  {
    code: "EASTLEA_MSASA",
    label: "Eastlea / Msasa",
    hint: "Eastlea Mall, Msasa industrial area",
  },
  {
    code: "BORROWDALE",
    label: "Borrowdale / Pomona",
    hint: "Borrowdale Village, Pomona Shopping Centre",
  },
];

export default function Register() {
  const [activeRole, setActiveRole] = useState("seller");
  const [form, setForm] = useState({
    name: "",
    phone: "",
    password: "",
    business_name: "",
    location_description: "",
    zone: "",
    product_type: "",
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (!form.zone) {
      toast.error("Please select a zone.");
      return;
    }
    setLoading(true);
    try {
      const payload = { role: activeRole, ...form };
      const data = await register(payload);
      // Check activeRole instead of undefined role
      if (data.activeRole === "rider") {
        navigate("/rider", { replace: true });
      } else {
        navigate("/seller", { replace: true });
      }
    } catch (err) {
      toast.error(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950 flex font-sans transition-colors">
      {/* Desktop Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-600 to-teal-900 relative overflow-hidden flex-col justify-center p-16 sticky top-0 h-screen">
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-white opacity-10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-96 h-96 bg-teal-400 opacity-20 rounded-full blur-3xl"></div>

        <div className="relative z-10 max-w-lg">
          <img
            src={`${process.env.PUBLIC_URL}/byka_logo.png`}
            alt="Byka Logo"
            className="h-28 mb-8 object-contain brightness-0 invert"
          />
          <h1 className="text-white text-4xl font-extrabold mb-6 tracking-tight leading-tight">
            Create an Account
          </h1>
          <p className="text-emerald-50 text-xl leading-relaxed opacity-95">
            Send anything, anywhere in Harare. From $2. Sign up in under a
            minute, or earn on your own schedule as a rider.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 relative bg-gray-50 lg:bg-white lg:dark:bg-zinc-900 overflow-y-auto transition-colors">
        {/* Mobile Header */}
        <div className="lg:hidden absolute top-0 left-0 w-full bg-emerald-500 flex flex-col items-center justify-center px-6 pt-12 pb-24 z-0 rounded-b-[40px] shadow-sm">
          <h1 className="text-white text-2xl font-bold">Create Account</h1>
          <p className="text-emerald-100 text-sm mt-1">
            Send anything, anywhere.
          </p>
        </div>

        {/* Form Card */}
        <div className="w-full max-w-md bg-white dark:bg-zinc-900 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] lg:shadow-none p-8 z-10 mt-28 lg:mt-0 border border-gray-100 dark:border-zinc-800 lg:border-none transition-colors">
          <div className="hidden lg:block mb-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              Sign Up
            </h2>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              Create your Byka account.
            </p>
          </div>

          {/* Role Tabs */}
          <div className="bg-gray-100 dark:bg-zinc-800 rounded-2xl p-1.5 flex mb-8 transition-colors">
            <button
              type="button"
              onClick={() => setActiveRole("seller")}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                activeRole === "seller"
                  ? "bg-white dark:bg-zinc-700 text-emerald-600 dark:text-emerald-400 shadow-sm ring-1 ring-gray-200 dark:ring-zinc-600"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200/50 dark:hover:bg-zinc-700/50"
              }`}
            >
              I'm a User
            </button>
            <button
              type="button"
              onClick={() => setActiveRole("rider")}
              className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                activeRole === "rider"
                  ? "bg-white dark:bg-zinc-700 text-emerald-600 dark:text-emerald-400 shadow-sm ring-1 ring-gray-200 dark:ring-zinc-600"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200/50 dark:hover:bg-zinc-700/50"
              }`}
            >
              I'm a Rider
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Full Name
              </label>
              <input
                type="text"
                value={form.name}
                onChange={set("name")}
                placeholder="Your full name"
                className="w-full px-4 py-3.5 bg-gray-50 dark:bg-zinc-850 border border-gray-200 dark:border-zinc-700 rounded-xl text-gray-900 dark:text-white focus:bg-white dark:focus:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Phone Number
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={set("phone")}
                placeholder="07XXXXXXXX"
                className="w-full px-4 py-3.5 bg-gray-50 dark:bg-zinc-850 border border-gray-200 dark:border-zinc-700 rounded-xl text-gray-900 dark:text-white focus:bg-white dark:focus:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Password
              </label>
              <input
                type="password"
                value={form.password}
                onChange={set("password")}
                placeholder="Min 6 characters"
                className="w-full px-4 py-3.5 bg-gray-50 dark:bg-zinc-850 border border-gray-200 dark:border-zinc-700 rounded-xl text-gray-900 dark:text-white focus:bg-white dark:focus:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-0.5">
                Where is your business based?
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Pick the area with your stall or storefront to assign the
                nearest rider.
              </p>

              <div className="grid grid-cols-2 gap-2">
                {ZONE_OPTIONS.map((z) => {
                  const isSelected = form.zone === z.code;
                  return (
                    <button
                      key={z.code}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, zone: z.code }))}
                      className={`p-3 rounded-xl border text-left transition-all relative ${
                        isSelected
                          ? "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/30 ring-2 ring-emerald-500"
                          : "border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-850 hover:border-gray-300 dark:hover:border-zinc-600"
                      }`}
                    >
                      <div className="font-semibold text-xs text-gray-900 dark:text-white mb-1">
                        {z.label}
                      </div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 leading-tight">
                        {z.hint}
                      </div>
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-4 h-4 bg-emerald-500 rounded-full flex items-center justify-center">
                          <svg
                            className="w-2.5 h-2.5 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white font-semibold py-3.5 rounded-xl text-lg transition-all disabled:opacity-70 mt-6 shadow-lg shadow-emerald-500/30"
            >
              {loading
                ? "Creating account..."
                : `Register as ${activeRole === "seller" ? "User" : "Rider"}`}
            </button>

            <p className="text-center text-gray-500 dark:text-gray-400 mt-8 text-sm">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-emerald-600 dark:text-emerald-400 font-semibold hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors"
              >
                Sign In
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
